/**
 * ForgotPasswordTemplate - Forgot Password Page Template
 *
 * Pure presentation component that composes the forgot password page.
 * Follows the Template + Wrapper pattern used for Login and Register pages.
 *
 * Architecture:
 * - Pure presentation (no business logic)
 * - Composes: Page title + ForgotPasswordForm + navigation links
 * - Used within AuthLayoutTemplate for two-column layout
 *
 * Features:
 * - Page title and description
 * - Forgot password form
 * - Link back to login
 *
 * Usage:
 * ```tsx
 * <AuthLayoutTemplate isWeb={true}>
 *   <ForgotPasswordTemplate
 *     onSubmit={handleForgotPassword}
 *     onNavigateToLogin={handleNavigateToLogin}
 *     isLoading={isLoading}
 *     isWeb={true}
 *   />
 * </AuthLayoutTemplate>
 * ```
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ForgotPasswordForm } from '../molecules/ForgotPasswordForm';

export interface ForgotPasswordTemplateProps {
    /** Callback when form is submitted with email */
    onSubmit: (email: string) => Promise<void>;

    /** Callback to navigate to login page */
    onNavigateToLogin?: () => void;

    /** Loading state during submission */
    isLoading?: boolean;

    /** Platform flag for styling (web vs plugin) */
    isWeb?: boolean;

    /** Initial error message to display */
    initialError?: string;
}

/**
 * ForgotPasswordTemplate
 *
 * Template component for the forgot password page.
 * Provides the complete UI for password reset requests.
 */
export const ForgotPasswordTemplate: React.FC<ForgotPasswordTemplateProps> = ({
    onSubmit,
    onNavigateToLogin,
    isLoading = false,
    isWeb = true,
    initialError,
}) => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.dir() === 'rtl';
    return (
        <div className="w-full max-w-md mx-auto px-6 py-8">
            {/* Page Title */}
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                    {onNavigateToLogin && (
                        <button
                            type="button"
                            onClick={onNavigateToLogin}
                            className="text-gray-900 hover:text-gray-700 transition-colors"
                            aria-label={t('auth.backToLoginAria')}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={isRtl ? { transform: 'scaleX(-1)' } : undefined}>
                                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    )}
                    <h1 className="text-2xl font-semibold text-gray-900">
                        {t('auth.forgotTitle')}
                    </h1>
                </div>
                <p className="text-sm text-gray-600">
                    {t('auth.forgotSubtitle')}
                </p>
            </div>

            {/* Forgot Password Form */}
            <ForgotPasswordForm
                onSubmit={onSubmit}
                isLoading={isLoading}
                isWeb={isWeb}
                initialError={initialError}
                onBackToLogin={onNavigateToLogin}
            />

            {/* Additional Help Text */}
            <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                    {t('auth.rememberPassword')}{' '}
                    {onNavigateToLogin && (
                        <button
                            type="button"
                            onClick={onNavigateToLogin}
                            className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
                        >
                            {t('auth.signIn')}
                        </button>
                    )}
                </p>
            </div>
        </div>
    );
};
